/**
 * Copy YouTube HTML Module
 * - Reads a YouTube URL from an input and converts it to youtube-nocookie embed URL
 * - Builds the video consent component HTML with data-iframe-url and copies it to the clipboard
 * - Shows temporary "Copied!" feedback on the button
 *
 * Use with: <div data-module="copy-youtube-html" class="s-custom-video_form">...</div>
 */

import { onDestroy } from "@/modules/_";

const VIDEO_HTML_TEMPLATE = `<div data-iframe-url="{iframeUrl}" data-service="Youtube" data-lenis-prevent data-category="Marketing" data-consent-given="false" class="video_component"><div class="video_consent-view"><svg viewBox="0 0 24 24" aria-hidden="true" class="video_consent-view_icon"><use href="#youtube"></use></svg><p class="max-w-xl"><span>Beim Laden des YouTube-Videos können Daten an YouTube/Google übertragen werden. Mit „Video laden“ willigst du ein. Mehr Infos in der </span><a href="#" target="_blank">Datenschutzerklärung</a><span>.</span></p><button data-wf--button--type="primary" aria-label="" data-consent-accept="youtube" class="button"><span class="button_size"><span data-el="button-text">Youtube laden</span></span></button></div></div>`;

/** Extract YouTube video ID from common URL formats. */
function getYoutubeVideoId(url) {
  if (!url || typeof url !== "string") return null;
  const trimmed = url.trim();
  if (!trimmed) return null;
  try {
    const parsed = new URL(trimmed.startsWith("http") ? trimmed : `https://${trimmed}`);
    const host = parsed.hostname.replace(/^www\./, "");
    if (host === "youtube.com" || host === "youtu.be") {
      if (host === "youtu.be") return parsed.pathname.slice(1).split("/")[0] || null;
      return parsed.searchParams.get("v");
    }
  } catch (_) {
    // fallback: simple regex for v= or youtu.be/
    const watchMatch = trimmed.match(/(?:youtube\.com\/watch\?.*[?&]v=|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/);
    if (watchMatch) return watchMatch[1];
    const shortMatch = trimmed.match(/(?:youtu\.be\/)([a-zA-Z0-9_-]{11})/);
    if (shortMatch) return shortMatch[1];
  }
  return null;
}

/** Build youtube-nocookie embed URL (no start/end for simplicity; can be extended). */
function toYoutubeNoCookieEmbedUrl(videoId, searchParams = null) {
  if (!videoId) return "";
  const url = new URL(`https://www.youtube-nocookie.com/embed/${videoId}`);
  if (searchParams && typeof searchParams.get === "function") {
    const start = searchParams.get("t") || searchParams.get("start");
    if (start) url.searchParams.set("start", start);
  }
  return url.toString();
}

/** Copy HTML string to clipboard as plain text (so it can be pasted into CMS/code). */
function copyTextToClipboard(text) {
  if (navigator.clipboard && window.isSecureContext) {
    return navigator.clipboard.writeText(text);
  }
  const ta = document.createElement("textarea");
  ta.value = text;
  ta.setAttribute("readonly", "");
  ta.style.position = "fixed";
  ta.style.opacity = "0";
  document.body.appendChild(ta);
  ta.select();
  try {
    document.execCommand("copy");
    return Promise.resolve();
  } finally {
    document.body.removeChild(ta);
  }
}

const COPIED_RESET_MS = 2000;

export default function (element, _dataset) {
  const input = element.querySelector('input[placeholder="Youtube URL"]') || element.querySelector(".form_field-input-text");
  const button = element.querySelector(".button");
  const buttonTextEl = element.querySelector('[data-el="button-text"]');

  if (!input || !button) {
    console.warn("copy-youtube-html: input or .button not found");
    return;
  }

  const originalLabel = (buttonTextEl && buttonTextEl.textContent.trim()) || "Copy HTML";

  function setButtonLabel(text) {
    if (buttonTextEl) buttonTextEl.textContent = text;
  }

  function handleCopy() {
    const rawUrl = (input.value || "").trim();
    const videoId = getYoutubeVideoId(rawUrl);
    if (!videoId) {
      setButtonLabel("Invalid URL");
      setTimeout(() => setButtonLabel(originalLabel), 2000);
      return;
    }

    let searchParams = null;
    try {
      const parsed = new URL(rawUrl.startsWith("http") ? rawUrl : `https://${rawUrl}`);
      searchParams = parsed.searchParams;
    } catch (_) {}

    const iframeUrl = toYoutubeNoCookieEmbedUrl(videoId, searchParams);
    const html = VIDEO_HTML_TEMPLATE.replace("{iframeUrl}", iframeUrl);

    copyTextToClipboard(html)
      .then(() => {
        setButtonLabel("Copied!");
        if (element._copyResetTimer) clearTimeout(element._copyResetTimer);
        element._copyResetTimer = setTimeout(() => {
          setButtonLabel(originalLabel);
          element._copyResetTimer = null;
        }, COPIED_RESET_MS);
      })
      .catch(() => {
        setButtonLabel("Error");
        setTimeout(() => setButtonLabel(originalLabel), 2000);
      });
  }

  button.addEventListener("click", handleCopy);
  const cleanup = () => {
    button.removeEventListener("click", handleCopy);
    if (element._copyResetTimer) clearTimeout(element._copyResetTimer);
  };
  onDestroy(cleanup);
}
